import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ExternalLink, Eye, Plus, Trash2, Upload, X } from "lucide-react";
import { Link } from "react-router-dom";
import { api, apiError, mediaUrl, youTubeId } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AdminBanner,
  AdminButton,
  AdminField,
  AdminSpinner,
  Drawer,
  adminInputClass,
} from "@/components/admin/AdminUi";
import MediaPicker from "@/components/admin/MediaPicker";

export const PLACEMENTS = [
  { value: "home", label: "Homepage (Stories & Videos)" },
  { value: "testimonials", label: "Homepage (What People Are Saying)" },
  { value: "emerald", label: "Emerald Honours" },
];

const MAX_UPLOAD_MB = 15;

const blank = (placement, nextOrder) => ({
  media_type: "youtube",
  title: "",
  url: "",
  video_url: "",
  description: "",
  caption: "",
  cta_label: "",
  cta_href: "",
  thumbnail_url: "",
  placements: placement ? [placement] : ["home"],
  enabled: true,
  sort_order: nextOrder,
});

/**
 * Single管理 surface for the canonical `videos` records. Used by the Videos library
 * screen and embedded inside Homepage → Stories & Videos; both read and write the
 * same API, so there is never a second copy of a story.
 */
export function StoriesVideosManager({ placement = null, lockPlacement = false, showLibraryLink = false }) {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState("");
  const [banner, setBanner] = useState({ status: "idle", message: "" });
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api
      .get("/admin/videos", { params: { ...(placement ? { placement } : {}), _: Date.now() } })
      .then((r) => setItems(r.data || []))
      .catch((e) => {
        setItems([]);
        setBanner({ status: "error", message: apiError(e) });
      });
  }, [placement]);

  useEffect(load, [load]);

  const payload = (row) => ({
    title: row.title,
    media_type: row.media_type === "upload" ? "upload" : "youtube",
    url: row.url || "",
    video_url: row.video_url || "",
    description: row.description || "",
    caption: row.caption || "",
    cta_label: row.cta_label || "",
    cta_href: row.cta_href || "",
    thumbnail_url: row.thumbnail_url || "",
    placements: row.placements || [],
    enabled: Boolean(row.enabled),
    sort_order: Number(row.sort_order) || 0,
  });

  const save = async () => {
    setFormError("");
    if (!editing.title.trim()) return setFormError("Give the story a title.");
    if (editing.media_type === "upload" && !editing.video_url) {
      return setFormError("Upload a video file, or switch to YouTube and paste a link.");
    }
    if (editing.media_type === "youtube" && !youTubeId(editing.url)) {
      return setFormError("That is not a YouTube link. Paste a youtube.com, youtu.be or Shorts address.");
    }
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/videos/${editing.id}`, payload(editing));
      else await api.post("/admin/videos", payload(editing));
      setBanner({ status: "ok", message: "Saved and published. It is live on the public site." });
      setEditing(null);
      load();
    } catch (e) {
      setFormError(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const patch = async (row, changes) => {
    try {
      await api.put(`/admin/videos/${row.id}`, payload({ ...row, ...changes }));
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const move = async (index, dir) => {
    const target = index + dir;
    if (!items || target < 0 || target >= items.length) return;
    const a = items[index];
    const b = items[target];
    try {
      await api.put(`/admin/videos/${a.id}`, payload({ ...a, sort_order: b.sort_order ?? target }));
      await api.put(`/admin/videos/${b.id}`, payload({ ...b, sort_order: a.sort_order ?? index }));
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete “${row.title}”? This removes it from every page.`)) return;
    try {
      await api.delete(`/admin/videos/${row.id}`);
      setBanner({ status: "ok", message: "Deleted." });
      load();
    } catch (e) {
      setBanner({ status: "error", message: apiError(e) });
    }
  };

  const uploadVideo = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setFormError(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB. Videos must be ${MAX_UPLOAD_MB} MB or smaller — for a longer film, upload it to YouTube and paste the link instead.`
      );
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setFormError("");
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/admin/media", body, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((f) => ({ ...f, video_url: `/api/media/${data.id}/file` }));
    } catch (e) {
      setFormError(apiError(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const posterOf = (row) =>
    (row.thumbnail_url && mediaUrl(row.thumbnail_url)) ||
    (row.youtube_id ? `https://i.ytimg.com/vi/${row.youtube_id}/hqdefault.jpg` : null);

  const previewId = editing && editing.media_type === "youtube" ? youTubeId(editing.url) : null;

  return (
    <div data-testid="stories-videos-manager">
      <AdminBanner status={banner.status} message={banner.message} onDismiss={() => setBanner({ status: "idle", message: "" })} />

      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-extrabold text-bka-navy">
          {placement === "home"
            ? "Homepage Stories & Videos"
            : placement === "testimonials"
            ? "Video Testimonials"
            : "All Stories & Videos"}
        </p>
        <div className="flex flex-wrap gap-2">
          {showLibraryLink && (
            <Link
              to="/admin/videos"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs font-extrabold text-slate-600 hover:border-bka-emerald hover:text-bka-emerald"
              data-testid="stories-open-library"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Open Full Videos Library
            </Link>
          )}
          {can("content.write") && (
            <AdminButton
              variant="emerald"
              className="px-4 py-2 text-xs"
              testId="stories-add"
              onClick={() => {
                setFormError("");
                setEditing(blank(placement, (items || []).reduce((m, r) => Math.max(m, r.sort_order ?? 0), 0) + 1));
              }}
            >
              <Plus className="h-3.5 w-3.5" /> Add Story / Video
            </AdminButton>
          )}
        </div>
      </div>

      {items === null ? (
        <AdminSpinner />
      ) : items.length === 0 ? (
        <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500" data-testid="stories-empty">
          Nothing here yet. Click “Add Story / Video”, paste a YouTube link or upload a video file, then publish.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="stories-grid">
          {items.map((row, index) => (
            <div key={row.id} className="overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`stories-row-${row.id}`}>
              <div className="relative aspect-video bg-slate-900">
                {posterOf(row) ? (
                  <img src={posterOf(row)} alt="" className="h-full w-full object-cover" loading="lazy" />
                ) : (
                  <video src={mediaUrl(row.video_url)} className="h-full w-full object-cover" preload="metadata" muted />
                )}
                <span className={`absolute right-2 top-2 rounded-full px-2.5 py-1 text-[11px] font-extrabold uppercase ${row.enabled ? "bg-emerald-600 text-white" : "bg-slate-500 text-white"}`}>
                  {row.enabled ? "Live" : "Hidden"}
                </span>
                <span className="absolute left-2 top-2 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-extrabold uppercase text-white">
                  {row.media_type === "upload" ? "Uploaded video" : "YouTube"}
                </span>
              </div>
              <div className="space-y-2 p-3">
                <p className="line-clamp-2 text-sm font-extrabold text-bka-navy">{row.title}</p>
                <p className="text-xs text-slate-500">
                  {(row.placements || []).map((p) => PLACEMENTS.find((x) => x.value === p)?.label || p).join(" · ") || "Not placed on any page"}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  <AdminButton variant="ghost" className="px-3 py-1.5 text-xs" testId={`stories-edit-${row.id}`} onClick={() => { setFormError(""); setEditing(row); }}>Edit</AdminButton>
                  <AdminButton variant="ghost" className="px-3 py-1.5 text-xs" testId={`stories-preview-${row.id}`} onClick={() => setPreviewing(row)}>
                    <Eye className="h-3.5 w-3.5" /> Preview
                  </AdminButton>
                  {can("content.write") && (
                    <>
                      <AdminButton variant="ghost" className="px-3 py-1.5 text-xs" testId={`stories-toggle-${row.id}`} onClick={() => patch(row, { enabled: !row.enabled })}>
                        {row.enabled ? "Hide" : "Show"}
                      </AdminButton>
                      <button type="button" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move earlier" data-testid={`stories-up-${row.id}`} className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald disabled:opacity-35">
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => move(index, 1)} disabled={index === items.length - 1} aria-label="Move later" data-testid={`stories-down-${row.id}`} className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-bka-emerald hover:text-bka-emerald disabled:opacity-35">
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" onClick={() => remove(row)} aria-label="Delete" data-testid={`stories-delete-${row.id}`} className="flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={Boolean(editing)}
        onClose={() => { setEditing(null); setFormError(""); }}
        title={editing?.id ? "Edit Story / Video" : "Add Story / Video"}
        testId="stories-drawer"
      >
        {editing && (
          <div className="space-y-5">
            <AdminField label="Where does the video come from?">
              <div className="flex gap-2">
                {[
                  { v: "youtube", l: "YouTube link" },
                  { v: "upload", l: "Upload a video file" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => setEditing((f) => ({ ...f, media_type: opt.v }))}
                    data-testid={`stories-type-${opt.v}`}
                    className={`rounded-xl border px-4 py-2 text-xs font-extrabold ${
                      editing.media_type === opt.v
                        ? "border-bka-emerald bg-bka-emerald/10 text-bka-emerald"
                        : "border-slate-200 text-slate-500 hover:border-bka-emerald"
                    }`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </AdminField>

            {editing.media_type === "youtube" ? (
              <>
                <AdminField label="YouTube link" required hint="Works with youtube.com/watch, youtu.be, Shorts and embed links.">
                  <input
                    value={editing.url || ""}
                    onChange={(e) => setEditing((f) => ({ ...f, url: e.target.value }))}
                    className={adminInputClass}
                    placeholder="https://www.youtube.com/watch?v=..."
                    data-testid="stories-url"
                  />
                </AdminField>
                {previewId ? (
                  <div className="overflow-hidden rounded-xl border border-slate-200" data-testid="stories-preview-frame">
                    <iframe src={`https://www.youtube-nocookie.com/embed/${previewId}`} title="Video preview" allowFullScreen className="aspect-video w-full" />
                  </div>
                ) : (
                  editing.url && <p className="text-xs font-semibold text-red-600" data-testid="stories-url-error">That is not a YouTube link yet.</p>
                )}
              </>
            ) : (
              <>
                <AdminField label="Video file" required hint={`MP4, up to ${MAX_UPLOAD_MB} MB. For longer films use YouTube.`}>
                  <input ref={fileRef} type="file" accept="video/mp4" onChange={uploadVideo} className="hidden" data-testid="stories-file-input" />
                  <AdminButton variant="ghost" testId="stories-file-button" onClick={() => fileRef.current?.click()} disabled={uploading}>
                    <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : editing.video_url ? "Replace video file" : "Choose video file"}
                  </AdminButton>
                </AdminField>
                {editing.video_url && (
                  <video src={mediaUrl(editing.video_url)} controls preload="metadata" className="aspect-video w-full rounded-xl bg-black" data-testid="stories-upload-preview" />
                )}
              </>
            )}

            <AdminField label="Thumbnail / featured image" hint={editing.media_type === "youtube" ? "Optional — the YouTube thumbnail is used when this is empty." : "Shown before the video plays."}>
              <MediaPicker
                kind="images"
                label=""
                value={editing.thumbnail_url || ""}
                onChange={(url) => setEditing((f) => ({ ...f, thumbnail_url: url }))}
                buttonLabel="Choose or Upload Thumbnail"
                testId="stories-thumbnail"
              />
            </AdminField>

            <AdminField label="Title" required>
              <input value={editing.title} onChange={(e) => setEditing((f) => ({ ...f, title: e.target.value }))} className={adminInputClass} data-testid="stories-title" />
            </AdminField>

            <AdminField label="Caption / role" hint="The small orange line under the title on the homepage card.">
              <input value={editing.caption || ""} onChange={(e) => setEditing((f) => ({ ...f, caption: e.target.value }))} className={adminInputClass} data-testid="stories-caption" />
            </AdminField>

            <AdminField label="Story / excerpt">
              <textarea rows={4} value={editing.description || ""} onChange={(e) => setEditing((f) => ({ ...f, description: e.target.value }))} className={adminInputClass} data-testid="stories-description" />
            </AdminField>

            <div className="grid gap-5 sm:grid-cols-2">
              <AdminField label="Button text (optional)">
                <input value={editing.cta_label || ""} onChange={(e) => setEditing((f) => ({ ...f, cta_label: e.target.value }))} className={adminInputClass} data-testid="stories-cta-label" />
              </AdminField>
              <AdminField label="Button link (optional)">
                <input value={editing.cta_href || ""} onChange={(e) => setEditing((f) => ({ ...f, cta_href: e.target.value }))} className={adminInputClass} data-testid="stories-cta-href" />
              </AdminField>
            </div>

            <AdminField label="Where should it appear?">
              <div className="space-y-2">
                {PLACEMENTS.map((p) => (
                  <label key={p.value} className="flex items-center gap-3 text-sm font-semibold text-bka-navy">
                    <input
                      type="checkbox"
                      checked={(editing.placements || []).includes(p.value)}
                      disabled={lockPlacement && p.value === placement}
                      onChange={(e) =>
                        setEditing((f) => ({
                          ...f,
                          placements: e.target.checked
                            ? [...(f.placements || []), p.value]
                            : (f.placements || []).filter((x) => x !== p.value),
                        }))
                      }
                      className="h-4 w-4 rounded border-slate-300"
                      data-testid={`stories-placement-${p.value}`}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </AdminField>

            <AdminField label="Display order" hint="Lower numbers appear first.">
              <input type="number" value={editing.sort_order ?? 0} onChange={(e) => setEditing((f) => ({ ...f, sort_order: e.target.value }))} className={adminInputClass} data-testid="stories-order" />
            </AdminField>

            <label className="flex items-center gap-3 text-sm font-semibold text-bka-navy">
              <input type="checkbox" checked={Boolean(editing.enabled)} onChange={(e) => setEditing((f) => ({ ...f, enabled: e.target.checked }))} className="h-4 w-4 rounded border-slate-300" data-testid="stories-enabled" />
              Published — visible on the website
            </label>

            {formError && (
              <p className="rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-700" data-testid="stories-form-error">{formError}</p>
            )}

            <AdminButton variant="emerald" testId="stories-save" onClick={save} disabled={saving || uploading}>
              {saving ? "Saving…" : "Save & Publish"}
            </AdminButton>
          </div>
        )}
      </Drawer>

      {previewing && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-6" role="dialog" aria-modal="true" aria-label="Video preview" data-testid="stories-preview-modal">
          <button type="button" onClick={() => setPreviewing(null)} aria-label="Close preview" data-testid="stories-preview-close" className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-white/50 text-white hover:bg-white hover:text-bka-navy">
            <X className="h-5 w-5" />
          </button>
          <div className="w-full max-w-3xl">
            {previewing.youtube_id ? (
              <iframe src={`https://www.youtube-nocookie.com/embed/${previewing.youtube_id}`} title={previewing.title} allowFullScreen className="aspect-video w-full rounded-xl" />
            ) : (
              <video src={mediaUrl(previewing.video_url)} controls autoPlay className="aspect-video w-full rounded-xl bg-black" />
            )}
            <p className="mt-3 text-center text-sm font-bold text-white">{previewing.title}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default StoriesVideosManager;
